import { format, isDate } from 'date-fns'
// eslint-disable-next-line import/no-extraneous-dependencies
import { jwtDecode } from 'jwt-decode'
import { ToastOptions, toast } from 'react-toastify'
import tenantConfig from 'config/tenant'
import { LoginUserData } from 'pages/login/types'
import { secureSetItem, secureGetItem, secureRemoveItem } from './secureStorage'

export const phoneRegExp = /^[\d\s\-()]+$/
export const timeRegex = /^([0-1]?\d|2[0-3]):[0-5]\d$/
export const REG_NUMBER_PATTERN = /^K[A-Z]{2}\s?\d{3}[A-Z]$/i

const getTokenKey = () => `${tenantConfig.storagePrefix}_authToken`
const getUserKey = () => `${tenantConfig.storagePrefix}_authUser`

export const userDetails = (user: LoginUserData) => {
  secureSetItem(getUserKey(), JSON.stringify(user))
}

export const getUser = (): LoginUserData | null => {
  const raw = secureGetItem(getUserKey())
  try {
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const removeUser = () => {
  secureRemoveItem(getUserKey())
}
export const saveToken = (token: string) => {
  secureSetItem(getTokenKey(), token)
}

export const getToken = (): string | null => {
  return secureGetItem(getTokenKey())
}

export const saveRefreshToken = (refreshToken: string) => {
  secureSetItem(`${tenantConfig.storagePrefix}_refreshToken`, refreshToken)
}

export const getRefreshToken = (): string | null => {
  return secureGetItem(`${tenantConfig.storagePrefix}_refreshToken`)
}

export const removeToken = () => {
  secureRemoveItem(getTokenKey())
  secureRemoveItem(`${tenantConfig.storagePrefix}_refreshToken`)
}

export const decodeToken = (token: string): Record<string, any> | null => {
  try {
    return jwtDecode(token)
  } catch (error) {
    return null
  }
}

export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true
  const currentTime = Date.now() / 1000
  return decoded.exp < currentTime
}

const TOAST_CONTAINER_IDS = ['default', 'modalToast', 'drawerToast']

const broadcastToast = (
  type: 'success' | 'info' | 'warning' | 'error',
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) => {
  const ids = options?.containerId ? [options.containerId] : TOAST_CONTAINER_IDS
  ids.forEach((containerId) => {
    toast[type](message, { ...options, containerId, autoClose: 3000 })
  })
}

export const notifySuccess = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) => broadcastToast('success', message, options)

export const notifyInfo = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) => broadcastToast('info', message, options)

export const notifyWarning = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) => broadcastToast('warning', message, options)

export const notifyError = (
  message: string | JSX.Element,
  options?: ToastOptions<unknown> | undefined
) => broadcastToast('error', message, options)
export const toDropdownOptions = <T, V = string>({
  data,
  valueKey,
  labelKey
}: {
  data: T[]
  valueKey?: keyof T
  labelKey?: keyof T
}): { value: V; label: string }[] => {
  return data.map((item) => {
    const value: V = valueKey
      ? (item[valueKey] as unknown as V)
      : (String(item) as V)
    const label = labelKey ? String(item[labelKey]) : String(item)
    return {
      value,
      label
    }
  })
}

export const formatDate = (date: string | null) => {
  if (!date) return ''

  try {
    return format(new Date(date), 'dd MMM yyyy')
  } catch {
    return ''
  }
}

export const formatHistoryDate = (date: string | null | undefined) => {
  if (!date) return '-'

  try {
    return format(new Date(date), 'dd MMM yyyy, hh:mm a')
  } catch {
    return '-'
  }
}

export const formatCurrency = (value?: number): string => {
  if (value == null) return '-'
  return `KES ${value.toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

export const formatDateAndTime = (date: string | null) => {
  if (!date) return ''

  try {
    return format(new Date(`${date}Z`), 'dd MMM yyyy @ hh:mm a')
  } catch {
    return ''
  }
}
export const formatDateRange = (
  from: string | Date | null,
  to: string | Date | null
) => {
  let fromDate: Date | undefined
  let toDate: Date | undefined
  if (from) {
    fromDate = isDate(from) ? (from as Date) : new Date(from)
    if (!to) return `After ${fromDate.toDateString()}`
  }

  if (to) {
    toDate = isDate(to) ? (to as Date) : new Date(to)
    if (!from) return `Before ${toDate.toDateString()}`
  }

  if (!fromDate || !toDate) return 'All Dates'

  if (
    fromDate.toString() === 'Invalid Date' ||
    toDate.toString() === 'Invalid Date'
  ) {
    return ''
  }

  const options = {
    year: 'numeric' as const,
    month: 'short' as const,
    day: '2-digit' as const
  }

  const dateTimeFormat = new Intl.DateTimeFormat('en', options)
  return dateTimeFormat.formatRange(fromDate, toDate)
}

export type DateTypes = Date | string | null | undefined

export const isValidDate = (dateObject: DateTypes): boolean => {
  if (!dateObject) return false
  return new Date(dateObject).toString() !== 'Invalid Date'
}

export const isValidDateRange = (from: DateTypes, to: DateTypes): boolean => {
  if (!from || !to) return false
  if (!isValidDate(from) || !isValidDate(to)) return false
  const fromDate = isDate(from) ? (from as Date) : new Date(from)
  const toDate = isDate(to) ? (to as Date) : new Date(to)

  if (toDate < fromDate) return false
  return true
}

export const toServerDateFormat = (date?: Date) => {
  return date ? format(date, 'yyyy-MM-dd') : undefined
}

export const toLowerCaseByKey =
  <T extends object>(key: keyof T) =>
  (object: T) =>
    (object[key] as string).toLowerCase()

export const getRandomString = (length: number): string => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const charactersLength = characters.length
  for (let i = 0; i < length; i += 1) {
    const { crypto } = window
    const array = new Uint32Array(1)
    const randomNum = crypto.getRandomValues(array)[0]
    const randomIndex = randomNum % charactersLength
    result += characters.charAt(randomIndex)
  }
  return result
}

export const getAPIValidationErrorMessage = (
  errorResponse?: Array<Record<string, Array<Record<string, any>>>> | null
): string => {
  let errorMsg = ''
  if (errorResponse) {
    const errorBody = errorResponse?.length > 0 ? errorResponse[0] : {}
    const errorKeys = Object.keys(errorBody)
    if (errorKeys.length) {
      errorMsg = `${errorKeys[0]} : ${errorBody[errorKeys[0]][0]?.errorDescription}`
    }
  }
  return errorMsg
}

interface FormValues {
  [key: string]: any
}

export const getChangedValues = (
  originalObj: FormValues,
  updatedObj: FormValues
): Partial<FormValues> => {
  const changes: Partial<FormValues> = {}

  for (const key in originalObj) {
    if (Object.prototype.hasOwnProperty.call(originalObj, key)) {
      if (
        originalObj[key as keyof FormValues] !==
        updatedObj[key as keyof FormValues]
      ) {
        changes[key as keyof FormValues] = updatedObj[key as keyof FormValues]
      }
    }
  }

  return changes
}

export const getModalTitleText = (isLoading: boolean, exp?: any): string => {
  if (!isLoading) {
    return exp || ''
  }
  return ''
}

// Format Kenyan Shilling values; by default we show any decimals returned from
// the backend instead of rounding to whole shillings.
export const formatKES = (
  amount: number,
  options?: { minDecimals?: number; maxDecimals?: number }
): string => {
  const value = amount ?? 0
  const decimalsInValue =
    typeof value === 'number' ? value.toString().split('.')[1]?.length ?? 0 : 0

  const minimumFractionDigits = options?.minDecimals ?? 0
  const maximumFractionDigits =
    options?.maxDecimals ?? Math.max(minimumFractionDigits, decimalsInValue)

  return `KES ${value.toLocaleString('en-KE', {
    minimumFractionDigits,
    maximumFractionDigits
  })}`
}

export enum ROLES_TYPE {
  SUPERADMIN = 'ROLE_SUPERADMIN',
  ADMIN = 'ROLE_ADMIN',
  AGENT = 'ROLE_AGENT',
  INTERNAL_USER = 'ROLE_INTERNAL_USER',
  USER = 'ROLE_USER'
}
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  MPESA: 'M-PESA',
  NGAO: 'CREDIT'
}

export interface ILabelValueOption {
  label: string
  value: string
}

// Payment mode filter options.
export const PAYMENT_MODE_OPTIONS: ILabelValueOption[] = [
  { label: 'Annual', value: 'ANNUAL' },
  { label: 'Monthly', value: 'MONTHLY' }
]

export const PAYMENT_METHOD_OPTIONS: ILabelValueOption[] = Object.entries(
  PAYMENT_METHOD_LABELS
).map(([value, label]) => ({ label, value }))

export enum IssuanceStatus {
  DRAFT = 'DRAFT',
  SUCCESS = 'SUCCESS',
  NTSA_SUCCESS = 'NTSA_SUCCESS',
  NTSA_FAILED = 'NTSA_FAILED'
}

export const stripLinks = (text?: string | null): string => {
  if (!text) return ''
  return text
    .split(/(?<=\.)\s+/)
    .filter((sentence) => !/https?:\/\//i.test(sentence))
    .join(' ')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export const triggerCertificateDownload = async (
  url: string,
  fileName: string
): Promise<boolean> => {
  const cleanUrl = url.trim().replace(/^["']+|["']+$/g, '')
  try {
    const response = await fetch(cleanUrl)
    if (!response.ok) throw new Error('Failed to fetch certificate file.')
    const blob = await response.blob()
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(objectUrl)
    return true
  } catch {
    window.open(cleanUrl, '_blank')
    return false
  }
}

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export const getDashboardPath = (): string => '/dashboard'

export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through to the legacy path below.
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    // Keep it out of view and out of the tab order while it is focused.
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand('copy')
    textarea.remove()
    return copied
  } catch {
    return false
  }
}
